import { FC, useEffect, useState } from "react"
import { PublishedTable } from "../PublishedTable/PublishedTable"
import BtButton from "../../bt-button/bt-button"
import { useLocation } from "react-router-dom"
import appState from "../../../state/AppStateContainer"
import { useTranslation } from "react-i18next"
import { ReactComponent as structureTreeIcon } from "../../../assets/svg/structureTree.svg";
import { ReactComponent as paperListIcon } from "../../../assets/svg/paperList.svg";
import { ReactComponent as SuccessSignUpIcon } from '../../../assets/svg/successSignUpIcon.svg';
import OpportunityCard from "../../../components/shared/OpportunityCard/OpportunityCard"
import serviceSales, { Subscription } from "../../../services/service-sales"
import { NotificationManager } from "react-notifications";
import { Loading } from "../../../components/shared"
import styles from './page-published-bid-requests-list.module.scss';
import Field from "../../../components/shared/Field/Field"
import Modal from "../../../components/shared/Modal/Modal"
import { isValidEmail } from "../../../util"
import { serviceOrganization } from "../../../services/service-organization"
import { logEvent } from "../../../services/eventsService"

type Props = {
  onlyPublic?: boolean,
  openToNewTab?: boolean,
}

export const PagePublishedBidRequestsList: FC<Props> = ({onlyPublic, openToNewTab}) => {
  const [t] = useTranslation('requests');
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const [loading, setLoading] = useState(false);
  const [prioSubscription, setPrioSubscription] = useState<Subscription>();
  const [publishedSubscription, setPublishedSubscription] = useState<Subscription>();
  const [submitting, setSubmitting] = useState(false);
  const [startSubscriptionShow, setStartSubscriptionShow] = useState(false);
  const [tableKey, setTableKey] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');

  const [errorMessages, setErrorMessages] = useState({
    invoiceEmail: '',
    invoiceRef: '',
  });

  const invoiceEmail: string = onlyPublic ? '' : appState.getInvoiceEmail();
  const invoiceRef: string = onlyPublic ? '' : appState.getInvoiceRef();

  const uuid = query.get('uuid') ?? appState.getOrCreateAnonymousUUID() ?? undefined;
  const productId = query.get('product-id')  ?? undefined;

  useEffect(() => {
    if (query.get('lang')) {
      appState.setLanguage(query.get('lang'));
    }
  }, [query]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      if ((onlyPublic || openToNewTab) && !productId) {
        setErrorMessage(t('missingProductId', 'Missing product id'));
        setLoading(false);
        return;
      } else if (productId) {
        try {
          const verifiedProductId = await serviceOrganization.verifyOnePlatformProductId(productId);
          appState.setProductId(verifiedProductId);
        } catch (e) {
          setErrorMessage(t('invalidProductId', 'Invalid product id'));
          setLoading(false);
          return;
        }
      }
      if (onlyPublic) {
        if (!query.get('uuid')) {
          const url = new URL(window.location.href);
          url.searchParams.set("uuid", uuid);
          window.history.replaceState({}, "", url);
        }
        await logEvent({
          event: 'OPENED_PUBLIC_PUBLISHED_QUOTATIONS',
          userIdentifier: uuid,
          metadata: {
            source: 'iframe',
            productId,
          },
          options: {
            unique: true,
          }
        });
        setLoading(false);
        return;
      } else if (openToNewTab) {
        await logEvent({
          event: 'OPENED_PUBLISHED_QUOTATIONS',
          metadata: {
            source: 'iframe',
            productId,
          },
          options: {
            unique: true,
          }
        });
      }
      try {
        const publishedSubscription = await serviceSales.fetchPublishedQuotationsSubscription();
        setPublishedSubscription(publishedSubscription);
        const prioSubscription = await serviceSales.fetchRecommendedSubscription();
        setPrioSubscription(prioSubscription);
      } catch (e) {
        NotificationManager.error((e as Error).toString(), t('notification.error', 'Kunde inte hämta förfrågningar'));
      }
      setLoading(false);
    })();
  }, []);

  const opportunityCards = [
    {
      id: 'win-bids',
      title: t('published.opportunityCards.winBids.title', 'Öka din chans att vinna anbud'),
      icon: structureTreeIcon,
      notFill: true,
      features: [
        {
          icon: SuccessSignUpIcon,
          title: t('published.opportunityCards.winBids.matching.title', 'Matchning'),
          description: t('published.opportunityCards.winBids.matching.description', 'Bevaknings och notiser göra att nya publicerade förfrågningar som matchar er profil fängas upp i tid.')
          
        },
        {
          icon: SuccessSignUpIcon,
          title: t('published.opportunityCards.winBids.bidFlow.title', 'Anbudsflöde'),
          description: t('published.opportunityCards.winBids.bidFlow.description', 'Läs underlag, ställ frågor och lämna anbud direkt i Accurator.')
        }
      ]
    },
    {
      id: 'competitive-advantage',
      title: t('published.opportunityCards.competitiveAdvantage.title', 'Få en fördel gentemot dina konkurrenter'),
      icon: paperListIcon,
      notFill: true,
      features: [
        {
          icon: SuccessSignUpIcon,
          title: t('published.opportunityCards.competitiveAdvantage.filters.title', 'Träffsäkra filter'),
          description: t('published.opportunityCards.competitiveAdvantage.filters.description', 'Sök och filtrera på kategori, län och tidsfrist för att snabbt hitta rätt jobb.'),
        },
        {
          icon: SuccessSignUpIcon,
          title: t('published.opportunityCards.competitiveAdvantage.quickOverview.title', 'Snabb överblick'),
          description: t('published.opportunityCards.competitiveAdvantage.quickOverview.description', 'Sortera på relevans och sista svarsdatum för att prioritera rätt förfrågningar'),
        }
      ]
    },
    {
      id: 'expand-customer-base',
      title: t('published.opportunityCards.expandCustomerBase.title', 'Utöka din kundbas'),
      icon: paperListIcon,
      notFill: true,
      features: [
        {
          icon: SuccessSignUpIcon,
          title: t('published.opportunityCards.expandCustomerBase.newCustomers.title', 'Nya beställare'),
          description: t('published.opportunityCards.expandCustomerBase.newCustomers.description', 'Hitta och nå inköpare ni inte arbetat med tidigare inom era kategorier och län.')
        },
        {
          icon: SuccessSignUpIcon,
          title: t('published.opportunityCards.expandCustomerBase.moreContacts.title', 'Fler kontakter'),
          description: t('published.opportunityCards.expandCustomerBase.moreContacts.description', 'Lämna anbud och bygg relationer via förfrågningar i Accurator.')
        }
      ]
    }
  ];

  const faqData = [
    {
      id: 'all-requests',
      question: t('published.faq.allRequests.question', 'Är det här alla förfrågningar?'),
      answer: t('published.faq.allRequests.answer', 'Nej. Publicerade visar de upphandlingar som inköpare valt att lägga ut öppet, normalt ca 10-20%. Övriga förfrågningar skickas direkt till utvalda leverantörer och kan bli tillgängliga med Accurator Prio abonnemang.')
    },
    {
      id: 'missing-info',
      question: t('published.faq.missingInfo.question', 'Varför ser jag inte vem som lagt upp förfrågan eller projektnamn?'),
      answer: t('published.faq.missingInfo.answer', 'Utan prenumeration visas bara kategori, län och anbudstid. Aktivera prenumerationen för att se beställare, projektdetaljer och förfrågningsunderlag och lämna anbud.')
    },
    {
      id: 'all-users',
      question: t('published.faq.allUsers.question', 'Behöver alla användare ett abonnemang?'),
      answer: t('published.faq.allUsers.answer', 'Nej. Publicerade förfrågningar gäller för hela företaget. Admin styr vilka användare som får tillgång och notiser.')
    },
    {
      id: 'frequency',
      question: t('published.faq.frequency.question', 'Hur ofta kommer nya förfrågningar in?'),
      answer: t('published.faq.frequency.answer', 'Löpande. Skapa bevakningar så får ni notiser när jobb som matchar era kategorier och län publiceras.')
    }
  ];

  const onSubscribe = async () => {
    if (onlyPublic) return;
    setSubmitting(true);
    const fields = {
      invoiceEmail,
      invoiceRef,
    };

    const errorMessages = Object.entries(fields).reduce((total, [key, value]) => {
      if (!value) {
        total[key] = t('validation.required', 'Detta fält är obligatoriskt');
      }
      if (key === 'invoiceEmail' && !isValidEmail(value)) {
        total[key] = t('validation.email', 'Felaktig e-postadress');
      }
      return total;
    }, {} as {[key: string]: string});

    if (Object.keys(errorMessages).length > 0) {
      setErrorMessages(errorMessages as {invoiceEmail: string, invoiceRef: string});
      setSubmitting(false);
      return;
    }
    const responseInvoiceEmail = await serviceOrganization.updateField("invoiceEmail", invoiceEmail);
    if (responseInvoiceEmail.invoiceEmail) {
      errorMessages.invoiceEmail = responseInvoiceEmail.invoiceEmail;
    }
    const responseInvoiceRef = await serviceOrganization.updateField("invoiceRef", invoiceRef);
    if (responseInvoiceEmail.invoiceRef) {
      errorMessages.invoiceRef = responseInvoiceRef.invoiceRef;
    }
    if (Object.keys(errorMessages).length > 0) {
      setErrorMessages(errorMessages as {invoiceEmail: string, invoiceRef: string});
      setSubmitting(false);
      return;
    }

    await appState.fetchOrganizations();

    const subscription = await serviceSales.orderPublishedQuotationsSubscription();
    setPublishedSubscription(subscription);
    if (openToNewTab) {
      await logEvent({
        event: 'ORDERED_PUBLISHED_QUOTATIONS_SUBSCRIPTION',
        userIdentifier: uuid,
        metadata: {
          source: 'iframe',
          productId,
        },
        options: {
          unique: true,
        }
      });
    }
    
    setTableKey(tableKey + 1);
    setErrorMessages({
      invoiceEmail: '',
      invoiceRef: '',
    });
    setStartSubscriptionShow(false);
    setSubmitting(false);
    NotificationManager.success(
      t('notification.success.ordered', 'Prenumerationen kommer förnyas automatiskt ett år i taget.'),
      t('notification.success.title', 'Sparat')
    );
  }

  const hasActiveSubscription = !!publishedSubscription?.active?.startDate || !!prioSubscription?.active?.startDate;

  if (loading) {
    return <div className={styles.loadingContainer}><Loading type='inline' /></div>;
  }

  if (errorMessage) {
    return <div className={styles.loadingContainer}>{errorMessage}</div>;
  }

  return (
    <div className={styles.container}>
      {!hasActiveSubscription && (
        <>
          <p style={{ fontSize: '14px', color: 'var(--gray-700)', marginBottom: '16px' }}>{t('published.description.text', 'En del av alla upphandlingar i Accurator publiceras öppet (cirka 10–20 %). Här söker inköpare nya leverantörer. Prenumerera för att söka, filtrera och bevaka jobb som matchar er profil och lämna anbud när det är rätt.')}</p>
          <div className={styles.opportunityCardsGrid}>
            {opportunityCards.map((card) => (
              <OpportunityCard
                key={card.id}
                title={card.title}
                icon={card.icon}
                features={card.features}
                notFill={card.notFill}
              />
            ))}
          </div>
        </>
      )}
      {(!hasActiveSubscription || onlyPublic) && (
        <div className={styles.pricingSection}>
          {!onlyPublic && (
            <div className={styles.price}>
              <span>{t('published.priceAmount', '799 kr')}</span>/{t('published.pricePeriod', 'månad')}
            </div>
          )}
          <BtButton
            style={{ width: 'fit-content' }}
            onClick={() => {
              if (onlyPublic) {
                logEvent({
                  event: 'CLICKED_CREATE_SUPPLIER_ACCOUNT',
                  userIdentifier: uuid,
                  metadata: {
                    source: 'iframe',
                    productId,
                  },
                  options: {
                    unique: true,
                  }
                }).then(() => {
                  let path = '/supplier-sign-up';
                  if (uuid) {
                    path += `?uuid=${uuid}`;

                    if (productId) {
                      path += `&product-id=${productId}`;
                    }
                  } else if (productId) {
                    path += `?product-id=${productId}`;
                  }
                  window.open(path, '_blank');
                })
              } else {
                setStartSubscriptionShow(true)
              }
            }}
          >
            {onlyPublic ? t('button.createAccount', 'Skapa Accurator-konto') : t('published.button', 'Aktivera')}
          </BtButton>
        </div>
      )}
      <PublishedTable onlyPublic={onlyPublic} openToNewTab={openToNewTab} key={`${tableKey}`} />
      {!hasActiveSubscription && (
        <div className={styles.faqSection}>
          <p>{t('published.faq.title', 'Vanliga frågor')}</p>
          <div>
            {faqData.map((faq) => (
              <div key={faq.id}>
                <div className={styles.question}>{faq.question}</div>
                <div className={styles.answer}>{faq.answer}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      <Modal
        title={t('published.subscription.heading', 'Beställ prenumeration')}
        show={startSubscriptionShow}
        setShow={setStartSubscriptionShow}
        buttonInfo={{ label: t('published.subscription.button', 'Bekräfta köp'), action: onSubscribe, loaderShow: submitting }}
      >
        <div style={{ fontSize: "14px", color: "var(--gray-700)", maxWidth: "500px" }}>
          {t('published.subscription.intro', 'Tjänsten "visa publicerade förfrågningar" är en prenumerationstjänst som betalas månadsvis och förnyas automatiskt med ett år i taget.')}

          <h3>{t('published.subscription.subheading', 'Beställning')}</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div>{t('published.subscription.product', 'Visa publicerade förfrågningar, 12 månader')}</div>
              <div>{t('published.subscription.price', '799 kr/månad')}</div>
            </div>
          </div>

          {(
            !appState.getCurrentOrg()?.invoiceEmail ||
            !appState.getCurrentOrg()?.invoiceRef
          ) && (
            <>
              <h3>{t('published.subscription.invoiceDetails.heading', 'Fakturauppgifter')}</h3>
              <p>{t('published.subscription.invoiceDetails.intro', 'Vi saknar era fakturauppgifter. Uppgifterna sparas och kommer användas även för framtida köp av tilläggstjänster.')}</p>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "8px" }}>
                <Field
                  label={t('published.subscription.invoiceDetails.label.email', 'E-postadress')}
                  value={invoiceEmail}
                  onChange={invoiceEmail => appState.setInvoiceEmail(invoiceEmail)}
                  errorMessage={errorMessages.invoiceEmail}
                  required
                />
                <Field
                  label={t('published.subscription.invoiceDetails.label.ref', 'Referens')}
                  value={invoiceRef}
                  onChange={invoiceRef => appState.setInvoiceRef(invoiceRef)}
                  errorMessage={errorMessages.invoiceRef}
                  required
                />
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  )
}